import { eventQBus } from "../../types/EventQBus";
import { fireFilterSubmit, unlockSubmitButtons } from "../FilterFormActions";
import type { TrackingLabels } from "../../tracking/TrackingLabels";
import DelayedAction from "../../util/DelayedAction";
import { MINMAX_FIELD_SELECTOR, MINMAX_RANGE_SELECTOR } from "./SliderSelectors";
import { FacetValue } from "../FacetValue";
import { isDesktopDevice } from "../../util/Utils";

const SLIDER_HIDDEN_INPUT_FIELD_CLASS = "ts_heureka_sliderHiddenInput";
const SLIDER_HIDDEN_INPUT_FIELD_SELECTOR = `.${SLIDER_HIDDEN_INPUT_FIELD_CLASS}`;
const SLIDER_CLASS = "ts_heureka_slider";
const SLIDER_SELECTOR = `.${SLIDER_CLASS}`;

/**
 *
 *
 *
 *
 *
 *
 *
 */
function updateSubmitField(
  slider: HTMLElement,
  field: HTMLInputElement,
  index: number,
  target: HTMLInputElement,
  filterMethod: TrackingLabels["san_FilterMethod"]
) {
  const values = (field.value || "_,_").split(",");
  let fieldWasChanged = false;
  if (values.length > index) {
    fieldWasChanged = !!(target.value && values[index] != target.value);
    values[index] = target.value || "_";
  }
  field.value = values.join(",");

  if (field.form) field.form.dataset["autoSubmit"] = "allowed";

  /*                                                                  */
  if (fieldWasChanged) {
    /*                                                                                 */
    unlockSubmitButton(field);
    field.dataset.heurekaSliderLastMethod = filterMethod;
    if (filterMethod === "slider_max" || filterMethod === "slider_min") {
      const availablePreselectsStr = slider.dataset.heurekaSliderAvailablePreselects;
      if (availablePreselectsStr) {
        const availablePreselects = Number(availablePreselectsStr);
        if (availablePreselects) {
          slider.dataset.heurekaSliderAvailablePreselects = `${availablePreselects - 1}`;
        } else {
          field.dataset.doNotSubmitPreselect = "true";
        }
      }
    }

    field.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

/**
 *
 *
 *
 */
function unlockSubmitButton(field: HTMLInputElement) {
  const form = field.closest("form");
  if (form) {
    unlockSubmitButtons.bind(form)();
  }
}

/**
 *
 *
 *
 */
function init({ id }: { id: string }) {
  const slider = <HTMLElement>document.getElementById(id);
  if (!slider || slider.dataset.heurekaSliderInitialized) {
    /*                   */
    return;
  }

  slider.dataset.heurekaSliderInitialized = "true";
  initAutoSubmit(slider);

  const submitField: HTMLInputElement | undefined | null = slider
    .closest(SLIDER_SELECTOR)
    ?.querySelector<HTMLInputElement>(SLIDER_HIDDEN_INPUT_FIELD_SELECTOR);

  if (submitField) {
    const minField = slider.querySelectorAll<HTMLInputElement>(MINMAX_FIELD_SELECTOR)[0];
    const maxField = slider.querySelectorAll<HTMLInputElement>(MINMAX_FIELD_SELECTOR)[1];
    if (minField) {
      initialiseMinInputs(slider, submitField, minField);
    }

    initialiseSliderRange(submitField, slider, minField, maxField);

    if (maxField) {
      initialiseMaxInputs(slider, submitField, maxField);
    }
  }
}

function fireSliderAutoSubmit(this: HTMLFormElement, filterMethod?: string) {
  /*                                                         */
  if (this.closest(".ts_heureka_inlinedFilterForm") && this.dataset["autoSubmit"] !== "forbidden") {
    if (filterMethod) {
      this.dataset.tsFeatureFilterMethod = filterMethod;
    }
    this.dataset["autoSubmit"] = "forbidden";
    return fireFilterSubmit(this);
  }
  return false;
}

function initAutoSubmit(slider: HTMLElement): void {
  const form = slider?.closest("form");
  if (form) {
    const delayedAction = DelayedAction.create(form, fireSliderAutoSubmit.bind(form, "adopt_delayed"));

    const doCancel = delayedAction.cancel.bind(delayedAction);
    const doDelay = delayedAction.delay.bind(delayedAction, {});
    const doDelayNoWait = delayedAction.delay.bind(delayedAction, {
      delayInMillis: 0,
      actionToPerform: fireSliderAutoSubmit.bind(form), /*                          */
    });
    const doThrottle = delayedAction.throttle.bind(delayedAction, {
      throttleInMillis: 50,
    });
    const options = { passive: true };

    /*                                                                                                  */
    slider.addEventListener("touchstart", doCancel, options);
    slider.addEventListener("mousedown", doCancel, options);
    slider.addEventListener("touchend", doDelay, options);
    slider.addEventListener("mouseup", doDelay, options);

    slider.addEventListener("focusin", doCancel, options);
    slider.addEventListener("focusout", doDelayNoWait, options);

    /*                                                                                                    */
    /*                                                                                                    */
    /*                                               */
    if (isDesktopDevice()) {
      form.addEventListener(
        "mousedown",
        (event) => {
          if (FacetValue.isFacetValueUserInteraction(event)) doThrottle();
        },
        options
      );
    }

    form.addEventListener(
      "click",
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.type !== "range") doCancel();
      },
      options
    );

    form.addEventListener("filterSubmit", doCancel, options);
    form.addEventListener("reset", doCancel, options);
    form.addEventListener("submit", () => (form.dataset["autoSubmit"] = "forbidden"), options);
  }
}

/**
 *
 *
 *
 *
 *
 *
 */
function initialiseSliderRange(
  submitField: HTMLInputElement,
  slider: HTMLElement,
  minField: HTMLInputElement | undefined | null,
  maxField: HTMLInputElement | undefined | null
) {
  const sliderRange = slider.querySelector<HTMLDivElement>(MINMAX_RANGE_SELECTOR)?.closest("div");
  sliderRange?.addEventListener(
    "click",
    () => {
      if (minField && minField.value != slider.dataset.sliderMinVal) {
        updateSubmitField(slider, submitField, 0, minField, "slider_min");
      }
      if (maxField && maxField.value != slider.dataset.sliderMaxVal) {
        updateSubmitField(slider, submitField, 1, maxField, "slider_max");
      }
    },
    {
      passive: true,
    }
  );
}

function initialiseMinInputs(slider: HTMLElement, submitField: HTMLInputElement, minField: HTMLInputElement) {
  const rangeSelector = slider.querySelectorAll<HTMLInputElement>(MINMAX_RANGE_SELECTOR)[0];
  initialiseInputField(slider, submitField, minField, "heurekaSliderInitialMinValue", rangeSelector, 0);
}

function initialiseMaxInputs(slider: HTMLElement, submitField: HTMLInputElement, maxField: HTMLInputElement) {
  const rangeSelector = slider.querySelectorAll<HTMLInputElement>(MINMAX_RANGE_SELECTOR)[1];
  initialiseInputField(slider, submitField, maxField, "heurekaSliderInitialMaxValue", rangeSelector, 1);
}

export function focusTracker(
  sanFacetActivity: TrackingLabels["san_FacetActivity"],
  sanFacetActivityType: TrackingLabels["san_FacetActivityType"]
): EventListener {
  return () => {
    eventQBus.emit("tracking.bct.submitEvent", {
      san_FacetActivity: sanFacetActivity,
      san_FacetActivityType: sanFacetActivityType,
    });
  };
}

export function deleteTracker(
  sanFacetActivity: TrackingLabels["san_FacetActivity"],
  sanFacetActivityType: TrackingLabels["san_FacetActivityType"]
): EventListener {
  return (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && !target.value) {
      eventQBus.emit("tracking.bct.submitEvent", {
        san_FacetActivity: sanFacetActivity,
        san_FacetActivityType: sanFacetActivityType,
      });
    }
  };
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
function initialiseInputField(
  slider: HTMLElement,
  submitField: HTMLInputElement,
  inputField: HTMLInputElement,
  limitKey: string,
  singleSlider: HTMLInputElement | undefined | null,
  limitIndex: number
) {
  const limitValue = slider.dataset[limitKey];
  const fieldType = limitIndex == 0 ? "min" : "max";
  const { facetName } = slider.dataset;
  if (limitValue) {
    inputField.value = limitValue;
    inputField.dispatchEvent(new Event("change"));
  }
  if (singleSlider) {
    singleSlider.addEventListener(
      "change",
      () => updateSubmitField(slider, submitField, limitIndex, inputField, `slider_${fieldType}`),
      {
        passive: true,
      }
    );
  }
  inputField.addEventListener(
    "change",
    () => updateSubmitField(slider, submitField, limitIndex, inputField, "interval"),
    {
      passive: true,
    }
  );
  if (facetName) {
    inputField.addEventListener("focusin", focusTracker(`click_${fieldType}`, facetName), {
      passive: true,
    });
    inputField.addEventListener("input", deleteTracker(`delete_${fieldType}`, facetName), {
      passive: true,
    });
  }
}

/**
 *
 */
export default function registerSliderHandlers() {
  eventQBus.on("assets.slider.initialised", init);
}
